import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "./AuthContext";
import { AlertTriangle, Send, X, Loader2, Flag } from "lucide-react";

// Fire after jobs are created anywhere: checkIdGaps({ jobIds, autoFlag, source })
export function checkIdGaps(detail) {
  window.dispatchEvent(new CustomEvent("idgaps:check", { detail }));
}

const STATUS = { PENDING_UPLOAD: "link sent", WAITING_FOR_REVIEW: "ready to review" };

export default function IdGapHost() {
  const [req, setReq] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [sel, setSel] = useState([]);
  const [flagged, setFlagged] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const on = async (e) => {
      const d = e.detail || {};
      const ids = (d.jobIds || []).filter(Boolean);
      if (!ids.length) return;
      try {
        const { data } = await axios.post(`${API_BASE}/voi/id-gaps`, { job_ids: ids });
        if (!data.gaps.length) return;
        let nFlag = 0;
        if (d.autoFlag) {
          const toFlag = data.gaps.filter((g) => g.reason === "new_client").map((g) => g.client_id);
          if (toFlag.length) nFlag = (await axios.post(`${API_BASE}/voi/bulk`, { client_ids: toFlag, action: "require" })).data.updated;
        }
        setGaps(data.gaps); setFlagged(nFlag); setReq(d);
        setSel(data.gaps.filter((g) => !g.open_check_status).map((g) => g.client_id));
      } catch { /* non-blocking */ }
    };
    window.addEventListener("idgaps:check", on);
    return () => window.removeEventListener("idgaps:check", on);
  }, []);

  if (!req) return null;
  const close = () => { setReq(null); setGaps([]); };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/voi/bulk`, { client_ids: sel, action: "send" });
      const msg = `ID checks: ${data.sent} sent${data.failed.length ? ` · ${data.failed.length} couldn't be emailed — copy their links from the ID check tab` : ""}`;
      (data.failed.length ? toast.warning : toast.success)(msg);
      close();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={close} data-testid="id-gap-dialog">
      <div className="bg-[var(--paper)] rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2 text-[14px] font-medium text-[#92400E]"><AlertTriangle size={15} /> ID check needed</div>
          <button onClick={close} data-testid="id-gap-close"><X size={17} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[13px] text-[var(--ink-2)]" data-testid="id-gap-summary">
            {req.source ? `${req.source}: ` : ""}{gaps.length} client{gaps.length === 1 ? " isn't" : "s aren't"} ID verified. The ATO/TPB expects identity to be verified before lodging.
          </p>
          {flagged > 0 && (
            <div className="text-[12px] text-[#92400E] inline-flex items-center gap-1" data-testid="id-gap-flagged">
              <Flag size={11} /> {flagged} new client{flagged === 1 ? " was" : "s were"} flagged — their next job will warn too.
            </div>
          )}
          <ul className="max-h-64 overflow-y-auto divide-y divide-[var(--border)] border border-[var(--border)] rounded-md">
            {gaps.map((g) => (
              <li key={g.client_id} className="flex items-center gap-2 px-3 py-2 text-[13px]" data-testid={`id-gap-row-${g.client_id}`}>
                <input type="checkbox" disabled={!!g.open_check_status} checked={sel.includes(g.client_id)}
                       onChange={() => toggle(g.client_id)} data-testid={`id-gap-select-${g.client_id}`} />
                <span className="flex-1 truncate">{g.name}</span>
                <span className="text-[11px] text-[var(--ink-3)]">
                  {g.open_check_status ? `check ${STATUS[g.open_check_status]}` : g.reason === "new_client" ? "new client" : "flagged"}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="px-5 py-3 border-t border-[var(--border)] flex justify-end gap-2">
          <button className="btn-ghost" onClick={close} data-testid="id-gap-skip">Not now</button>
          <button className="btn-primary" onClick={send} disabled={busy || !sel.length} data-testid="id-gap-send">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send ID check{sel.length === 1 ? "" : "s"} ({sel.length})
          </button>
        </div>
      </div>
    </div>
  );
}
